import * as md5 from "js-md5";

interface LocaleMap {
  [key: string]: string;
}

interface LocaleNotFoundCallback {
  (key: string): void;
}

// 工具函数
function isUpperHex32(s: string): boolean {
  return typeof s === "string" && /^[A-F0-9]{32}$/.test(s);
}

function normalizeLangMap(map: LocaleMap): LocaleMap {
  const out: LocaleMap = {};
  for (const k in map) {
    const key = isUpperHex32(k) ? k : md5.md5(k).toUpperCase();
    out[key] = map[k];
  }
  return out;
}

function evaluateSafeExpr(expr: string, args: any): string {
  if (expr == null) return "";
  let e = String(expr).trim();

  if (!/^[\w$.\\[\]\s?]+$/.test(e)) return "";
  e = e.replace(/\?\./g, ".").replace(/\s+/g, "");
  let i = 0;
  const len = e.length;

  function readIdent(pos: number): { val: string; next: number } | null {
    const m = /^[A-Za-z_$][\w$]*/.exec(e.slice(pos));
    return m ? { val: m[0], next: pos + m[0].length } : null;
  }

  function readNumber(pos: number): { val: number; next: number } | null {
    const m = /^\d+/.exec(e.slice(pos));
    return m ? { val: Number(m[0]), next: pos + m[0].length } : null;
  }

  function readIndex(pos: number): { val: number; next: number } | null {
    if (e[pos] !== "[") return null;
    const m = /^\[(\d+)\]/.exec(e.slice(pos));
    return m ? { val: Number(m[1]), next: pos + m[0].length } : null;
  }

  let root: any;
  const start = readIdent(i);
  if (start) {
    i = start.next;
    if (start.val === "p") {
      root = args;
    } else {
      if (args != null && typeof args === "object" && start.val in args) {
        root = args[start.val];
      } else {
        return "";
      }
    }
  } else {
    const num = readNumber(i);
    if (num) {
      i = num.next;
      if (Array.isArray(args)) root = args[num.val];
      else return "";
    } else {
      return "";
    }
  }

  while (i < len) {
    const ch = e[i];
    if (ch === ".") {
      i++;
      const id2 = readIdent(i);
      if (!id2) return "";
      root = root == null ? undefined : root[id2.val];
      i = id2.next;
    } else if (ch === "[") {
      const idx = readIndex(i);
      if (!idx) return "";
      root = root == null ? undefined : root[idx.val];
      i = idx.next;
    } else {
      return "";
    }
    if (root === undefined || root === null) {
      // 忽略错误
    }
  }

  return root == null ? "" : String(root);
}

function safeTemplateRender(template: string, args: any): string {
  if (typeof template !== "string") return template;
  return template.replace(/\$\{([^{}]+)\}/g, (_, inner) => {
    const v = evaluateSafeExpr(inner, args);
    if (v !== "") return v;

    if (inner.trim() === "p") return args == null ? "" : String(args);

    return "";
  });
}

// 全局变量声明已移至 src/types/global.d.ts

// 初始化全局变量
global.$localeMap = {};
global.$localeNotFound = null;

global.$locale = (key: string, args?: any): string => {
  const ret = _locale(key, args) || "";
  // const { minBetAmount } = getCommonContext();
  // ret = ret.replace("#{minBetAmount}", (minBetAmount || "1") + "");
  return ret;
};

function _locale(key: string, args?: any): string {
  if (!key) return "";
  const hash = md5.md5(key).toUpperCase();
  let v = global.$localeMap[hash];

  if (!v) {
    v = key;
    if (typeof global.$localeNotFound === "function") {
      try {
        global.$localeNotFound(key);
      } catch {
        // 忽略错误
      }
    }
  }
  if (args === undefined) return v;

  try {
    return safeTemplateRender(v, args);
  } catch {
    return v;
  }
}

function renderLocale(
  template: string,
  renderFun: (items: string[]) => any
): any {
  template = global.$locale(template);
  const items = String(template).split("##");
  return renderFun(items);
}

function register(
  langMap: LocaleMap,
  onNotFound?: LocaleNotFoundCallback
): void {
  global.$localeMap = normalizeLangMap(langMap);
  global.$localeNotFound = onNotFound || null;
}
function globalIsInit(): boolean {
  return global.$localeMap && Object.keys(global.$localeMap).length > 0;
}
// 默认导出
const locale = {
  get: global.$locale,
  render: renderLocale,
  register,
  globalIsInit,
};

export default locale;
